/**
 * Sponsor order history (Milestone 07, Sprint 06 T6.5).
 *
 * Lists the orders the signed-in user sponsored: service name, beneficiary
 * public name, amount, status, date. Displays only public profile fields —
 * never beneficiary PII beyond the public candidate name.
 *
 * @see Documentation/Specification/020_Definitions/application-agnostic/ledger/ServiceOrder.md
 * @see sprints/2026-10/Milestone_07_SponsoredServices/sprint-06-bluepages-sponsor-ui/README.md
 */
import {
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardTitle,
  IonIcon,
  IonSpinner,
} from '@ionic/react';
import { ribbon } from 'ionicons/icons';
import {
  useMySponsoredOrders,
  useCandidateProfile,
  useLegalEntity,
  useServiceCatalogueEntry,
} from '@/lib/api/hooks';

function ServiceName({ serviceKey }: { serviceKey: string }) {
  const { data } = useServiceCatalogueEntry(serviceKey);
  return <>{data?.data?.name ?? serviceKey}</>;
}

/**
 * Beneficiary public name (M07 S06 T6.5): resolved via the public profile's
 * legal entity — the same derivation the search index uses. Falls back to the
 * public headline. No other beneficiary fields are rendered.
 */
function BeneficiaryName({ candidateId }: { candidateId: string }) {
  const { data: profileEnvelope } = useCandidateProfile(candidateId);
  const profile = profileEnvelope?.data;
  const legalEntityId = profile?.legalEntity?.id ?? '';
  const { data: legalEntityEnvelope } = useLegalEntity(legalEntityId);
  const entity = legalEntityEnvelope?.data;

  let name = '';
  if (entity) {
    name =
      entity.companyName ||
      entity.tradingName ||
      [entity.firstName, entity.lastName].filter(Boolean).join(' ') ||
      '';
  }
  if (!name) name = profile?.headline || '';
  return <>{name || 'Candidate'}</>;
}

const STATUS_COLOR: Record<string, string> = {
  Paid: 'var(--ion-color-warning)',
  Fulfilled: 'var(--ion-color-success)',
  PendingPayment: 'var(--ion-color-medium)',
  Cancelled: 'var(--ion-color-medium-shade)',
  Expired: 'var(--ion-color-medium-shade)',
  Failed: 'var(--ion-color-danger)',
  Refunded: 'var(--ion-color-danger)',
};

export function SponsorOrderHistory() {
  const { data, isLoading } = useMySponsoredOrders();
  const orders = data?.data ?? [];

  return (
    <IonCard>
      <IonCardHeader>
        <IonCardTitle style={{ color: '#f1f5f9' }}>
          <IonIcon icon={ribbon} style={{ color: '#60a5fa' }} /> Sponsored services
        </IonCardTitle>
      </IonCardHeader>
      <IonCardContent>
        {isLoading ? (
          <div style={{ textAlign: 'center', padding: '0.5rem 0' }}>
            <IonSpinner name="dots" />
          </div>
        ) : orders.length === 0 ? (
          <p style={{ color: 'var(--bp-text-muted)', fontSize: '0.85rem', margin: 0 }}>
            You have not sponsored any services yet. Open a candidate profile to
            sponsor their identity verification.
          </p>
        ) : (
          <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
            {orders.map((order) => (
              <li
                key={order._id}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 8,
                  alignItems: 'baseline',
                  fontSize: '0.85rem',
                  borderBottom: '1px solid rgba(148, 163, 184, 0.15)',
                  paddingBottom: 8,
                }}
              >
                <span>
                  <ServiceName serviceKey={order.serviceKey} />
                  {' — '}
                  <BeneficiaryName candidateId={order.beneficiaryRef.id} />
                </span>
                <span style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                  R{order.amount.value.toFixed(2)}
                  {' · '}
                  <strong style={{ color: STATUS_COLOR[order.status] ?? 'inherit' }}>
                    {order.status}
                  </strong>
                  <br />
                  <span style={{ opacity: 0.7 }}>
                    {new Date(order.metadata.createdAt).toLocaleDateString()}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        )}
      </IonCardContent>
    </IonCard>
  );
}
